import React, { useCallback, useMemo, useRef, useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useAuth } from "@/auth/useAuth";
import { dailyApi } from "@/features/daily/api/daily.api";
import { useDailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import { dailyFiltersAdapter, useDailyFilters } from "@/features/daily/hooks/use-daily-filters";
import { DailyResourceStatus, useDailyResource } from "@/features/daily/hooks/use-daily-resource";
import { DailyStateBlock, DailySectionState } from "@/features/daily/components/DailyStateBlock";
import { DailyHeaderMeta } from "@/features/daily/components/DailyHeaderMeta";
import { SkipLink } from "@/features/daily/components/SkipLink";
import { AttentionPanel } from "@/features/daily/components/command/AttentionPanel";
import { EventsDrawer } from "@/features/daily/components/command/EventsDrawer";
import { LiveBar } from "@/features/daily/components/command/LiveBar";
import { StatusStrip } from "@/features/daily/components/command/StatusStrip";
import { TodayHourlyCard } from "@/features/daily/components/command/TodayHourlyCard";
import { TodayKpis } from "@/features/daily/components/command/TodayKpis";
import { LiveSessionPhone } from "@/features/daily/components/command/LiveSessionPhone";
import { isStatusTodayDto, todayViewModel } from "@/features/daily/components/command/today-view-model";
import { commandViewModel, dailyFormat, isPlatformStatusDto } from "@/features/daily/components/command/command-view-model";
import { healthViewModel, isEventsDto } from "@/features/daily/components/command/health-view-model";
import { CommandCenterErrorsRow } from "@/features/observability/components/CommandCenterErrorsRow";
import { frontendErrorsFilters } from "@/features/observability/components/frontend-errors-filters";
import { sessionDiagnosticsAccess } from "@/features/session-diagnostics/access/session-diagnostics-access";
import type { FiltersBarLabels } from "@/features/orders-analytics/components/FiltersBar";
import "@/features/daily/styles/daily.css";
import "@/features/daily/styles/command-center.css";

// The replay library only loads when the live phone is on screen.
const LiveSessionReplayer = React.lazy(() => import("@/features/session-diagnostics/player/RrwebReplayer"));

const BODY_ID = "daily-command-main";
const POLL_MS = 60_000;
const EVENTS_LIMIT = "20";

const HEADER_LABELS: FiltersBarLabels = {
    clientSelector: "Clientes",
    allClients: "Todos los clientes",
    lastFullMonth: "Último mes completo",
    monthToDate: "Mes en curso",
    customRange: "Rango libre",
    refresh: "Actualizar",
    refreshing: "Actualizando…",
};

// L44: the resource status maps to a section state exhaustively — a state this map
// forgot could only ever surface as a silently blank section.
const SECTION_STATE: Record<DailyResourceStatus, DailySectionState> = {
    idle: "loading",
    loading: "loading",
    ok: "ok",
    partial: "partial",
    stale: "stale",
    error: "error",
    forbidden: "forbidden",
};

const SECTION_FAILURE: Record<"error" | "forbidden", string> = {
    error: commandViewModel.qualityLabel("error"),
    forbidden: "No disponible para tu cuenta",
};

export function CommandCenterPage() {
    const { state: authState } = useAuth();
    const { query, set } = useDailyQueryState();
    // D105: the app's workspace + range bar. Salud stays live ("today") until someone picks a range.
    const { filters, onFiltersChange } = useDailyFilters(query, set, "today");

    const viewerId = authState.user === null ? "" : authState.user.id;
    // D125: the operator can pause the live refresh; the page then asks for nothing on its own.
    const [auto, setAuto] = useState(true);
    const pollMs = auto ? POLL_MS : undefined;

    // L354/FR-009: the live window is asked for by PRESET, never as a timestamp
    // pinned when the page mounted. A tab left open past midnight therefore starts
    // receiving the new day the moment it polls, instead of calling yesterday "today".
    const scopeKey = query.scope.join(",");
    const typesKey = query.types.join(",");
    const params = useMemo(() => ({
        window: query.windowPreset ?? "today",
        from: query.windowPreset === "custom" ? query.windowFrom ?? "" : "",
        to: query.windowPreset === "custom" ? query.windowTo ?? "" : "",
        scope: scopeKey,
        types: typesKey,
    }), [query.windowPreset, query.windowFrom, query.windowTo, scopeKey, typesKey]);

    const eventsParams = useMemo(() => ({
        limit: EVENTS_LIMIT,
        scope: params.scope,
        types: params.types,
    }), [params.scope, params.types]);

    const fetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getStatus(params, { signal }),
        [params],
    );

    const eventsFetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getEvents(eventsParams, { signal }),
        [eventsParams],
    );

    const resource = useDailyResource(
        { resource: "status", scope: params.scope, query: params },
        fetcher,
        { identity: viewerId, isData: isPlatformStatusDto, pollMs },
    );

    // B16 (D134): today hour by hour, its funnel and the operations to look at first.
    const todayParams = useMemo(() => ({ scope: params.scope, types: params.types }), [params.scope, params.types]);
    const todayFetcher = useCallback(
        (signal: AbortSignal) => dailyApi.getStatusToday(todayParams, { signal }),
        [todayParams],
    );
    const todayResource = useDailyResource(
        { resource: "status-today", scope: todayParams.scope, query: todayParams },
        todayFetcher,
        { identity: viewerId, isData: isStatusTodayDto, pollMs },
    );
    const [eventsOpen, setEventsOpen] = useState(false);
    const eventsTriggerRef = useRef<HTMLElement | null>(null);
    const openEvents = useCallback((trigger: HTMLElement): void => {
        eventsTriggerRef.current = trigger;
        setEventsOpen(true);
    }, []);
    const closeEvents = useCallback((): void => setEventsOpen(false), []);

    const eventsResource = useDailyResource(
        { resource: "events", scope: eventsParams.scope, query: eventsParams },
        eventsFetcher,
        { identity: viewerId, isData: isEventsDto, pollMs },
    );

    const dto = resource.data;
    const statusVm = dto === null ? null : commandViewModel.status(dto);
    // D36: el permiso viaja CON el payload y lo decide el servidor que lo aplica.
    const canOperate = dto !== null && commandViewModel.canOperate(dto);
    const busy = resource.status === "loading" || resource.status === "idle";
    // FR-024: a background refresh never blanks a section that already has evidence
    // on screen (the Refresh button carries the in-flight feedback instead), but a
    // FAILED refresh must say so while keeping the previous figures and their age.
    const sectionState: DailySectionState = dto !== null && busy ? "ok" : SECTION_STATE[resource.status];

    const hrefFor = (extra: Readonly<Record<string, string>>): string =>
        commandViewModel.diagnosticHref({ scope: params.scope, types: params.types, ...extra });
    const inProgressHref = hrefFor({ queue: "day", filter: "in_progress" });
    const overdueHref = hrefFor({ queue: "overdue", window: params.window, from: params.from, to: params.to });

    const toggleAuto = (): void => {
        // Resuming catches up at once instead of showing figures as old as the pause.
        if (!auto) {
            resource.refresh();
            eventsResource.refresh();
            todayResource.refresh();
        }
        setAuto(!auto);
    };
    // D144 (like D143): the footer's source and evidence notes live in the state pill's (i).
    const sourceNotes = [
        `Consulta realizada: ${dailyFormat.stamp(resource.meta.fetchedAt)}`,
        `Evidencia de: ${dailyFormat.stamp(resource.meta.sourceAsOf)}`,
        ...(resource.meta.stale ? ["La evidencia está desactualizada"] : []),
        ...(statusVm === null ? [] : [`Evidencia: ${statusVm.qualityLabel} · ${statusVm.coverageLabel} · ${statusVm.policyLabel}`]),
    ];
    const today = todayResource.data;
    const healthy = dto !== null && healthViewModel.isHealthy(dto);

    const events = eventsResource.data === null ? null : healthViewModel.events(eventsResource.data.events);
    const eventsFailed = eventsResource.data === null
        && (eventsResource.status === "error" || eventsResource.status === "forbidden");
    // vb-metrics#38: the error cards follow the one client the bar selects; otherwise the workspace that reports them.
    // A client the errors screen does not support gets a neutral row from the cards themselves, never a guess.
    const errorsWorkspaceId = query.scope.length === 1
        ? dailyFiltersAdapter.canonicalClient(query.scope[0])
        : frontendErrorsFilters.SUPPORTED_WORKSPACES[0];

    const liveWorkspaceId = sessionDiagnosticsAccess.can(authState) ? sessionDiagnosticsAccess.configuredWorkspace() : null;
    // vb-metrics#38: the phone's column exists only while it follows a session; otherwise the error chart spans it.
    const [phoneStreaming, setPhoneStreaming] = useState(false);
    const streaming = liveWorkspaceId !== null && phoneStreaming;

    const pill = dto === null ? null : healthViewModel.statusPill(dto);
    const attention = dto !== null && healthViewModel.needsAttention(dto);

    return (
        <Shell title="Command Center" subtitle="Salud de la plataforma">
            <div className={healthy ? "daily-page daily-page--cc daily-page--ok" : "daily-page daily-page--cc"}>
                <SkipLink targetId={BODY_ID} />
                <PageHeader
                    title="Salud de la plataforma"
                    lede="Qué funciona ahora y qué necesita atención."
                    right={(
                        <DailyHeaderMeta
                            chips={null}
                            filters={filters}
                            onFiltersChange={onFiltersChange}
                            busy={busy}
                            onRefresh={resource.refresh}
                            labels={HEADER_LABELS}
                        />
                    )}
                />

                <LiveBar
                    fetchedAt={resource.meta.fetchedAt}
                    auto={auto}
                    onToggleAuto={toggleAuto}
                    pollSeconds={POLL_MS / 1000}
                    pill={pill === null ? null : { ...pill, tooltip: [...pill.tooltip, ...sourceNotes] }}
                    counts={dto === null ? null : healthViewModel.causeCounts(dto)}
                    events={events}
                    eventsFailed={eventsFailed}
                    onOpenEvents={openEvents}
                />

                <div className="daily-body" id={BODY_ID} tabIndex={-1}>
                    <DailyStateBlock
                        state={sectionState}
                        label="Salud de la plataforma"
                        detail={resource.error === null ? undefined : resource.error.message}
                        onRetry={resource.refresh}
                    >
                        {dto === null ? null : (
                            <div className="daily-cc">
                                <StatusStrip
                                    strip={healthViewModel.strip(dto)}
                                    services={healthViewModel.services(commandViewModel.capabilities(dto.capabilities))}
                                />
                                <TodayKpis
                                    tiles={healthViewModel.todayTiles(dto, {
                                        overdue: overdueHref,
                                        open: inProgressHref,
                                        historic: hrefFor({ queue: "historic_stuck" }),
                                        backlog: hrefFor({ queue: "credit_backlog" }),
                                    })}
                                    live={dto.envelope.window.label === "today"}
                                    queueHref={inProgressHref}
                                    conversions={today === null ? [] : todayViewModel.funnel(today)}
                                    conversionsFailure={todayResource.status === "error" || todayResource.status === "forbidden" ? SECTION_FAILURE[todayResource.status] : null}
                                />
                                <div className="daily-cc-board" data-attention={attention} data-live={liveWorkspaceId !== null} data-streaming={streaming}>
                                <div className="daily-cc-activity" data-attention={attention}>
                                    {today === null ? (
                                        // Guardian P3: a failed or refused read says so; the section never just vanishes.
                                        todayResource.status === "error" || todayResource.status === "forbidden" ? (
                                            <DailyStateBlock
                                                state={todayResource.status}
                                                label="Actividad de hoy"
                                                detail={todayResource.error === null ? undefined : todayResource.error.message}
                                                onRetry={todayResource.refresh}
                                            >
                                                {null}
                                            </DailyStateBlock>
                                        ) : null
                                    ) : (
                                        <TodayHourlyCard vm={todayViewModel.hourly(today, today.envelope.evaluatedAt)} />
                                    )}
                                    {attention ? (
                                        <AttentionPanel
                                            register={healthViewModel.register(dto)}
                                            incidents={commandViewModel.incidents(dto.incidents, { canOperate, viewerId })}
                                            viewerId={viewerId}
                                            onApplied={resource.refresh}
                                            causes={healthViewModel.causes(dto)}
                                            counts={healthViewModel.attentionCounts(dto)}
                                        />
                                    ) : null}
                                </div>
                                <div className="daily-cc-errors-line" data-live={liveWorkspaceId !== null} data-streaming={streaming}>
                                    {resource.meta.fetchedAt === null ? null : (
                                        <CommandCenterErrorsRow workspaceId={errorsWorkspaceId} refreshedAt={resource.meta.fetchedAt} attention={attention} />
                                    )}
                                    {liveWorkspaceId === null ? null : <LiveSessionPhone workspaceId={liveWorkspaceId} Player={LiveSessionReplayer} onStreamingChange={setPhoneStreaming} />}
                                </div>
                                </div>
                            </div>
                        )}
                    </DailyStateBlock>
                </div>
                {eventsOpen ? (
                    <EventsDrawer events={events} failed={eventsFailed} onClose={closeEvents} restoreFocusTo={eventsTriggerRef.current} />
                ) : null}
            </div>
        </Shell>
    );
}
